import { useMutation, useQuery } from '@tanstack/react-query';

import {
    applySubstitutions,
    downloadBatchFile,
    mapFacilityIdToApiParam,
    searchBatches,
    updateSubstitutionsForBatches,
} from 'api/warehouse/batches';

export const batchQueryKeys = {
    all: ['batches'],
    /** @param {number | string | null} facilityId */
    /** @param {Object | null | undefined} filters */
    searchByFacility: (facilityId, filters) => ['batches', 'v1', 'search', facilityId, filters ?? {}],
};

/**
 * @param {Object} [args={}]
 * @param {number | string | undefined | null} [args.facilityId]
 * @param {Object} [args.filters={}]
 * @param {boolean} [args.enabled=true]
 * @param {Omit<import('@tanstack/react-query').UseQueryOptions<Array, Error, Array, readonly unknown[]>, 'queryKey' | 'queryFn'>} [queryOptions={}]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array, Error>}
 */
export function useBatchesSearch({ enabled = true, facilityId, filters = {}, ...queryOptions } = {}) {
    const normalizedFacilityId = mapFacilityIdToApiParam(facilityId);
    const normalizedFilters = filters ?? {};

    return useQuery({
        queryKey: batchQueryKeys.searchByFacility(normalizedFacilityId ?? null, normalizedFilters),
        queryFn: async ({ signal }) => {
            const response = await searchBatches(
                { facilityId: normalizedFacilityId, ...normalizedFilters },
                { signal },
            );
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch batches');
            }
            const rows = response?.data?.rows;
            if (!Array.isArray(rows)) {
                throw new Error('Batches response missing rows array');
            }
            return rows;
        },
        enabled: normalizedFacilityId !== undefined && enabled,
        staleTime: 30 * 1000,
        ...queryOptions,
    });
}

/**
 * Downloads the batch's original print artifact. Rebuilding PDFs and pcdata
 * orders CSVs can take a while, so callers should surface the pending state.
 *
 * @param {import('@tanstack/react-query').UseMutationOptions<Object, Error, string | number>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, string | number>}
 */
export function useDownloadBatchFile(mutationOptions = {}) {
    return useMutation({
        mutationFn: async (batchId) => {
            const response = await downloadBatchFile(batchId);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to download batch file');
            }
            return response?.data ?? {};
        },
        ...mutationOptions,
    });
}

/**
 * Enqueues substitutions for a batch (`POST .../batches/update-substitutions` with one id).
 *
 * @param {import('@tanstack/react-query').UseMutationOptions<Object, Error, string | number>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, string | number>}
 */
export function useApplySubstitutions(mutationOptions = {}) {
    return useMutation({
        mutationFn: async (batchId) => {
            const response = await applySubstitutions(batchId);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to apply substitutions');
            }
            return response?.data ?? {};
        },
        ...mutationOptions,
    });
}
/**
 * Updates substitutions for selected batches (`POST .../batches/update-substitutions`).
 *
 * @param {import('@tanstack/react-query').UseMutationOptions<Object, Error, Array<string | number>>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, Array<string | number>>}
 */
export function useUpdateSubstitutionsForBatches(mutationOptions = {}) {
    return useMutation({
        mutationFn: async (batchIds) => {
            const response = await updateSubstitutionsForBatches(batchIds);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to apply substitutions');
            }
            return response?.data ?? {};
        },
        ...mutationOptions,
    });
}
